import { urenTussen } from './datum';
import type { ISODate, Instellingen, Taak, Werkvorm } from './types';

export type FilterStatus =
  | 'Actie nodig'
  | 'Gepland'
  | 'Herplannen'
  | 'Klaar'
  | 'Niet klaar'
  | 'Nog niet gepland'
  | 'Overleg'
  | 'Wacht op terugmelding'
  | 'Zelf ingepland';

export const FILTER_STATUSSEN: FilterStatus[] = [
  'Actie nodig',
  'Gepland',
  'Herplannen',
  'Klaar',
  'Niet klaar',
  'Nog niet gepland',
  'Overleg',
  'Wacht op terugmelding',
  'Zelf ingepland',
];

export type StatusToon = 'oranje' | 'blauw' | 'groen' | 'rood' | 'bruin' | 'accent';

export interface TaakStatusInfo {
  label: string;
  toon: StatusToon;
  filter: FilterStatus;
  actieNodig: boolean;
}

const afspraakWeg = (t: Taak) => (t.outlookMelding ?? '').startsWith('Afspraak verwijderd');

/** SPEC §3.5 */
export function taakStatus(t: Taak, vandaag: ISODate): TaakStatusInfo {
  const vervolg = t.tmAfgehandeld && (t.terugmelding === 'NIET_KLAAR' || t.terugmelding === 'HERPLANNEN') ? ' · vervolg' : '';
  let r: Omit<TaakStatusInfo, 'actieNodig'>;
  if (t.status !== 'GEPLAND') r = { label: 'Niet gepland', toon: 'oranje', filter: 'Nog niet gepland' };
  else if (t.taakType === 'FOCUS') r = { label: '🎯 Focusblok', toon: 'blauw', filter: 'Gepland' };
  else if (t.taakType === 'OVERLEG' && !afspraakWeg(t))
    r = t.bron === 'AGENDA'
      ? { label: '✍ Zelf ingepland', toon: 'blauw', filter: 'Zelf ingepland' }
      : { label: '💬 Overleg', toon: 'blauw', filter: 'Overleg' };
  else if (t.terugmelding === 'KLAAR') r = { label: '✓ Klaar', toon: 'groen', filter: 'Klaar' };
  else if (afspraakWeg(t)) r = { label: '⚠ Afspraak verwijderd', toon: 'rood', filter: 'Actie nodig' };
  else if (t.terugmelding === 'NIET_KLAAR') r = { label: '⏳ Niet klaar', toon: 'oranje', filter: 'Niet klaar' };
  else if (t.terugmelding === 'HERPLANNEN') r = { label: '🔁 Herplannen', toon: 'rood', filter: 'Herplannen' };
  else if (t.einddatum && t.einddatum < vandaag) r = { label: 'Wacht op terugmelding', toon: 'bruin', filter: 'Wacht op terugmelding' };
  else r = { label: 'Gepland', toon: 'accent', filter: 'Gepland' };
  const actieNodig =
    r.filter === 'Nog niet gepland' ||
    afspraakWeg(t) ||
    ((t.terugmelding === 'NIET_KLAAR' || t.terugmelding === 'HERPLANNEN') && !t.tmAfgehandeld);
  return { ...r, label: r.label + vervolg, actieNodig };
}

export function matchtFilter(t: Taak, filter: FilterStatus | 'Alle statussen', vandaag: ISODate): boolean {
  if (filter === 'Alle statussen') return true;
  const s = taakStatus(t, vandaag);
  if (filter === 'Actie nodig') return s.actieNodig;
  return s.filter === filter;
}

/** SPEC §3.2 – netto uren per dag (bruto minus pauze). */
export function dagUren(van: string, tot: string, cfg: Pick<Instellingen, 'pauzeMin' | 'pauzeVanaf'>): number {
  const bruto = urenTussen(van, tot);
  const pauze = bruto > (cfg.pauzeVanaf ?? 5.5) ? (cfg.pauzeMin ?? 30) / 60 : 0;
  return Math.max(0, bruto - pauze);
}

/** SPEC §3.6 – werkvorm afgeleid van de taakcode, of null als de code niets zegt. */
export function werkvormVoorCode(code?: string | null): Werkvorm | null {
  if (!code) return null;
  const c = code.trim().toLowerCase();
  const nr = c.split(/\s+/)[0];
  if (nr === '1014' || nr === '8734' || /remote\s*$/.test(c)) return 'REMOTE';
  if (nr === '1015' || nr === '8735' || /onsite\s*$/.test(c)) return 'ONSITE';
  return null;
}

/** Taakcodes uit de instelling (één per regel, "1015 - Installatie onsite"). */
export function taakcodes(cfg: Pick<Instellingen, 'taakcodes'>): { code: string; label: string }[] {
  return (cfg.taakcodes || '')
    .split('\n')
    .map((r) => r.trim())
    .filter(Boolean)
    .map((r) => ({ code: r.split(/\s+/)[0], label: r }));
}

/**
 * Normaliseert een change-/incidentnummer naar 'SC 2610 03253' (weergave). Accepteert met of zonder spaties.
 * Geeft de invoer ongewijzigd terug als het patroon niet herkend wordt.
 */
export function normaliseerChangeNr(invoer?: string | null): string {
  const s = (invoer ?? '').replace(/\s+/g, '').toUpperCase();
  const m = s.match(/^(SC|SI)(\d{4})(\d{4,6})$/);
  if (!m) return (invoer ?? '').trim();
  return `${m[1]} ${m[2]} ${m[3]}`;
}

export function compactChangeNr(invoer?: string | null): string {
  return (invoer ?? '').replace(/\s+/g, '').toUpperCase();
}

/** Zoekt "[code]" in een afspraakonderwerp: projectnummer of change-/incidentnummer. */
export function codeUitOnderwerp(onderwerp: string): string | null {
  const m = onderwerp.match(/\[([^\]]+)\]/);
  if (!m) return null;
  const c = m[1].trim();
  if (/^(SC|SI)\s*\d/i.test(c)) return normaliseerChangeNr(c);
  return c;
}

/** Vult een URL-sjabloon met {projectnummer}, {taakcode}, {changenr}, {changenrCompact}. */
export function vulSjabloon(sjabloon: string | null | undefined, v: Record<string, string | null | undefined>): string | null {
  if (!sjabloon) return null;
  return sjabloon.replace(/\{(\w+)\}/g, (_, k) => encodeURIComponent(v[k] ?? ''));
}
